import {useMutation} from '@tanstack/react-query'

import {
  SlotValidationMutationResult,
  UseSlotValidationMutationParams
} from '../../../../slotManagement/context/SlotManagementContext'
import {useSlotManagementContext} from '../../../../slotManagement/context/useSlotManagementContext'
import {SlotValidationResponse} from '../../../../slotManagement/requests/types'

import {useOrderChangeSlotValidationEnabled} from './useOrderChangeSlotValidationEnabled'

/**
 * Order Change slot validation mutation hook.
 * Provides on-demand validation for a single dateTime (e.g., on save).
 *
 * Gets configuration from SlotManagementContext (feature toggle, orgUnitId, siteNumber, orderId).
 *
 * Validation logic (same as query variant):
 * 1. Skip if order is auto-confirmable (no dispatch approval needed)
 * 2. Skip if no slot-managed fields are dirty
 * 3. Otherwise validation can be triggered
 *
 * Mutation is disabled when:
 * - Feature toggle is off
 * - Order is auto-confirmable
 * - No slot-managed fields are dirty
 * - Slot request params cannot be built (validation failed)
 * - No mutation function provided
 *
 * Side effect: Updates slot availability state in context based on validation result.
 *
 * @example
 * ```tsx
 * const {mutation, shouldValidate, slotAvailabilities} = useOrderChangeSlotValidationMutation({
 *   slotValidationMutationFn: (params) => validateSlot(params)
 * })
 *
 * // Trigger validation on save
 * const handleSave = async () => {
 *   if (shouldValidate) {
 *     // No params needed - built internally using useSlotRequest
 *     await mutation.mutateAsync()
 *   }
 * }
 * ```
 */
export const useOrderChangeSlotValidationMutation = ({
  slotValidationMutationFn
}: UseSlotValidationMutationParams): SlotValidationMutationResult<SlotValidationResponse> => {
  const {setSlotAvailability, slotAvailabilities} = useSlotManagementContext()

  // Determine if validation should be enabled (same logic as query variant)
  const {isEnabled, slotRequestResult} = useOrderChangeSlotValidationEnabled()

  // Determine if mutation should be used
  const shouldValidate = isEnabled && !!slotValidationMutationFn && !!slotRequestResult.params

  // Create single mutation for single slot validation
  const mutation = useMutation<SlotValidationResponse, unknown, void>({
    mutationFn: async () => {
      if (!slotValidationMutationFn || !slotRequestResult.params) {
        throw new Error('slotValidationMutationFn and slot params are required')
      }

      // Validate single slot using built-in params (Order Change always has 1 slot)
      return await slotValidationMutationFn(slotRequestResult.params)
    },
    onSuccess: (response: SlotValidationResponse) => {
      // Update slot availability (always index 0 for Order Change)
      setSlotAvailability({
        dateTimeIndex: 0,
        isAvailable: response.valid
      })
    },
    onError: () => {
      // Mark as unavailable on error
      setSlotAvailability({
        dateTimeIndex: 0,
        isAvailable: false
      })
    }
  })

  return {
    mutation,
    shouldValidate,
    slotAvailabilities
  }
}
